import React, { useState, useEffect, useCallback } from 'react';
import { Play, RefreshCw, Globe, Settings } from 'lucide-react';
import { testAllDnsServers, calculateStatistics, groupResultsByIsp, startScheduledRefresh } from './services/mockBackend';
import { DnsResult, DnsStatistics, GroupedResults } from './types/dns';
import StatisticsPanel from './components/StatisticsPanel';
import IspGroup from './components/IspGroup';
import LoadingSpinner from './components/LoadingSpinner';
import './App.css';

const App: React.FC = () => {
  const [domain, setDomain] = useState<string>('shop.villamarket.com');
  const [results, setResults] = useState<DnsResult[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [isBackgroundLoading, setIsBackgroundLoading] = useState<boolean>(false);
  const [lastTestTime, setLastTestTime] = useState<Date | null>(null);
  const [autoRefresh, setAutoRefresh] = useState<boolean>(false);
  const [refreshInterval, setRefreshInterval] = useState<number>(300); // 5 minutes in seconds

  const runDnsTest = useCallback(async (showLoading: boolean = true): Promise<void> => {
    if (showLoading) {
      setIsLoading(true);
    } else {
      setIsBackgroundLoading(true);
    }
    
    try {
      const testResults = await testAllDnsServers(domain);
      setResults(testResults);
      setLastTestTime(new Date());
    } catch (error) {
      console.error('DNS test failed:', error);
    } finally {
      if (showLoading) {
        setIsLoading(false);
      } else {
        setIsBackgroundLoading(false);
      }
    }
  }, [domain]);

  // Auto-refresh functionality
  useEffect(() => {
    let interval: NodeJS.Timeout;
    if (autoRefresh && refreshInterval > 0) {
      interval = setInterval(() => {
        runDnsTest(false); // Run in background without loading spinner
      }, refreshInterval * 1000);
    }
    return () => {
      if (interval) clearInterval(interval);
    };
  }, [autoRefresh, refreshInterval, domain, runDnsTest]);

  // Run initial test
  useEffect(() => {
    runDnsTest();
  }, [runDnsTest]);

  // Start scheduled refresh
  useEffect(() => {
    const handleScheduledResults = (results: DnsResult[]) => {
      setResults(results);
      setLastTestTime(new Date());
    };

    startScheduledRefresh(handleScheduledResults);
  }, []);

  const stats: DnsStatistics = calculateStatistics(results);
  const groupedResults: GroupedResults = groupResultsByIsp(results);

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100">
      {/* Header */}
      <header className="bg-white shadow-sm border-b">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center py-4">
            <div className="flex items-center space-x-3">
              <Globe className="w-8 h-8 text-blue-600" />
              <div>
                <h1 className="text-2xl font-bold text-gray-900">Villa DNS Statistics</h1>
                <p className="text-sm text-gray-600">
                  Monitor DNS resolution across Thai ISPs
                  {isBackgroundLoading && (
                    <span className="ml-2 text-green-600">• Background refresh in progress</span>
                  )}
                  <span className="ml-2 text-blue-600">• Auto-refresh every 5 minutes</span>
                </p>
              </div>
            </div>
            <div className="flex items-center space-x-4">
              <div className="flex items-center space-x-2">
                <Settings className="w-5 h-5 text-gray-500" />
                <label className="text-sm text-gray-600">Auto-refresh:</label>
                <input
                  type="checkbox"
                  checked={autoRefresh}
                  onChange={(e) => setAutoRefresh(e.target.checked)}
                  className="rounded"
                />
                {autoRefresh && (
                  <select
                    value={refreshInterval}
                    onChange={(e) => setRefreshInterval(Number(e.target.value))}
                    className="text-sm border rounded px-2 py-1"
                  >
                    <option value={60}>1m</option>
                    <option value={300}>5m</option>
                    <option value={600}>10m</option>
                    <option value={1800}>30m</option>
                  </select>
                )}
              </div>
            </div>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Controls */}
        <div className="bg-white rounded-xl shadow-lg p-6 mb-8">
          <div className="flex flex-col sm:flex-row gap-4 items-start sm:items-center justify-between">
            <div className="flex-1">
              <label htmlFor="domain" className="block text-sm font-medium text-gray-700 mb-2">
                Domain to test
              </label>
              <input
                id="domain"
                type="text"
                value={domain}
                onChange={(e) => setDomain(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                placeholder="Enter domain name"
              />
            </div>
            <div className="flex space-x-3">
              <button
                onClick={() => runDnsTest(true)}
                disabled={isLoading}
                className="flex items-center space-x-2 bg-blue-600 hover:bg-blue-700 disabled:bg-blue-400 text-white px-4 py-2 rounded-md transition-colors"
              >
                {isLoading ? (
                  <RefreshCw className="w-4 h-4 animate-spin" />
                ) : (
                  <Play className="w-4 h-4" />
                )}
                <span>{isLoading ? 'Testing...' : 'Test Now'}</span>
              </button>
            </div>
          </div>
          
          {lastTestTime && (
            <div className="mt-4 text-sm text-gray-600">
              Last test: {lastTestTime.toLocaleString()}
              <div className="mt-1 text-xs text-blue-600">
                Testing domains: shop.villamarket.com, villamarket.com, google.com
              </div>
              {autoRefresh && (
                <span className="ml-2 text-blue-600">
                  • Auto-refresh every {Math.floor(refreshInterval / 60)}m
                  {isBackgroundLoading && (
                    <span className="ml-1 text-green-600">• Background refresh in progress...</span>
                  )}
                </span>
              )}
            </div>
          )}
        </div>

        {/* Statistics Panel */}
        {results.length > 0 && (
          <div className="mb-8">
            <StatisticsPanel stats={stats} />
          </div>
        )}

        {/* Results */}
        {isLoading ? (
          <LoadingSpinner message="Testing DNS servers across Thai ISPs..." />
        ) : results.length > 0 ? (
          <div className="space-y-6">
            <h2 className="text-2xl font-bold text-gray-800 mb-6">Results by ISP</h2>
            {Object.entries(groupedResults).map(([ispName, ispResults]) => (
              <IspGroup
                key={ispName}
                ispName={ispName}
                results={ispResults}
              />
            ))}
          </div>
        ) : (
          <div className="text-center py-12">
            <Globe className="w-16 h-16 text-gray-400 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-gray-600 mb-2">No test results yet</h3>
            <p className="text-gray-500">Click "Test Now" to start monitoring DNS resolution</p>
          </div>
        )}
      </main>

      {/* Footer */}
      <footer className="bg-white border-t mt-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          <div className="text-center text-gray-600">
            <p>Villa DNS Statistics - Monitoring DNS resolution across Thai ISPs</p>
            <p className="text-sm mt-1">Built with React 18 & TypeScript</p>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default App;
